import type { OperationOrder } from '../components/OrderOperationsPanel';

/** Coffee-shop tuned: flag orders not moving or not picked up quickly. */
export const STUCK_MIN = 10;
export const MISSED_READY_MIN = 15;

export function minutesSince(iso: string | null | undefined): number {
  if (!iso) return 0;
  return (Date.now() - new Date(iso).getTime()) / 60000;
}

export function minutesBetween(start: string, end: string): number {
  return (new Date(end).getTime() - new Date(start).getTime()) / 60000;
}

export function isCancelledIssue(o: OperationOrder): boolean {
  return o.status === 'cancelled';
}

/** Open order in current status longer than STUCK_MIN (uses status_updated_at when present). */
export function isStuckIssue(o: OperationOrder): boolean {
  if (['completed', 'cancelled'].includes(o.status)) return false;
  const anchor = o.status_updated_at || o.created_at;
  return minutesSince(anchor) > STUCK_MIN;
}

/** Ready too long (live) or spent too long in ready before completion (historical). */
export function isLongReadyIssue(o: OperationOrder): boolean {
  if (!o.ready_at) {
    if (o.status === 'ready') {
      return minutesSince(o.created_at) > MISSED_READY_MIN;
    }
    return false;
  }
  if (o.status === 'ready') {
    return minutesSince(o.ready_at) > MISSED_READY_MIN;
  }
  if (o.status === 'completed' && o.completed_at) {
    return minutesBetween(o.ready_at, o.completed_at) > MISSED_READY_MIN;
  }
  return false;
}

export function isOrderIssue(o: OperationOrder): boolean {
  return isCancelledIssue(o) || isStuckIssue(o) || isLongReadyIssue(o);
}

export type IssueHeatmapFilter = 'all' | 'cancelled' | 'stuck' | 'long_ready';

export function matchesIssueHeatmapFilter(
  o: OperationOrder,
  filter: IssueHeatmapFilter
): boolean {
  if (filter === 'all') return isOrderIssue(o);
  if (filter === 'cancelled') return isCancelledIssue(o);
  if (filter === 'stuck') return isStuckIssue(o);
  return isLongReadyIssue(o);
}

/** Hour bucket (local) for heatmap — uses event time when timestamps exist. */
export function issueHeatmapHour(o: OperationOrder, filter: IssueHeatmapFilter): number {
  let iso = o.created_at;
  if (filter === 'cancelled' || (filter === 'all' && isCancelledIssue(o))) {
    iso = o.cancelled_at || o.created_at;
  } else if (filter === 'long_ready' || (filter === 'all' && isLongReadyIssue(o))) {
    iso = o.ready_at || o.created_at;
  } else if (filter === 'stuck' || (filter === 'all' && isStuckIssue(o))) {
    iso = o.status_updated_at || o.created_at;
  }
  return new Date(iso).getHours();
}
